import { MouseEvent, ReactNode, useMemo } from 'react';

import Box from '@mui/material/Box';
import find from 'lodash-es/find';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { getParticipantRegistrationState, registrationSignupAt } from 'src/helpers/participantList';
import {
  formatRegistrationSignupInfo,
  getParticipantDisplayAge,
  getRegistrationDisplayPrice,
} from 'src/helpers/registrationPrice';
import { canRegisterParticipant } from 'src/helpers/userCheckinWizard';
import { ICompetition } from 'src/interfaces/Competition';
import { IEnrichedParticipant } from 'src/interfaces/Participant';
import { IRegistration } from 'src/interfaces/Registration';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import useCheckinsStore from 'src/stores/checkinsStore';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import { DASHBOARD_GRID_ROW_HEIGHT } from 'src/common/components/dataGrid/useDashboardGridHeight';

/** Extra row height when contact details are shown (email, city, gender + divider). */
export const PARTICIPANT_DETAIL_ROW_EXTRA_HEIGHT = 64;

/** Extra row height when the missing date-of-birth warning is shown under the name. */
export const PARTICIPANT_DOB_WARNING_ROW_EXTRA_HEIGHT = 22;

export function needsDateOfBirthWarning(
  participant: IEnrichedParticipant,
  callbacks: ParticipantCellCallbacks,
): boolean {
  return (
    !!callbacks.register &&
    getParticipantRegistrationState(participant) === 'unregistered' &&
    !canRegisterParticipant(participant)
  );
}

export function ParticipantPrimaryRowBand({
  children,
  isExpanded,
}: {
  children: ReactNode;
  isExpanded: boolean;
}) {
  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        minHeight: DASHBOARD_GRID_ROW_HEIGHT,
        height: isExpanded ? DASHBOARD_GRID_ROW_HEIGHT : '100%',
      }}
    >
      {children}
    </Box>
  );
}

export type ParticipantCellCallbacks = {
  checkin?: (id?: string, e?: MouseEvent<unknown>) => void;
  register?: (id?: string, e?: MouseEvent<unknown>) => void;
  editRegistration?: (id?: string, e?: MouseEvent<unknown>) => void;
  deleteRegistration?: (id?: string, e?: MouseEvent<unknown>) => void;
  registerTooltip?: string;
  registrationId?: string;
  tourRegisterAnchorParticipantId?: string;
  tourQrAnchorParticipantId?: string;
};

export type ParticipantCellProps = {
  participant: IEnrichedParticipant;
  callbacks: ParticipantCellCallbacks;
};

export function useParticipantRowModel(
  participant: IEnrichedParticipant,
  callbacks: ParticipantCellCallbacks,
) {
  const {
    age: ageFromParticipant,
    checkin: checkinData,
    checkedIn,
    dateOfBirth,
    firstName,
    lastName,
    registration,
    discounted,
    id,
  } = participant;

  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { activeEditionId, competitions, dateFormat, distances, editionsById } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );
  const { checkinsById } = useCheckinsStore(checkinsState => checkinsState);

  const displayAge = useMemo(() => {
    const edition =
      activeEditionId && editionsById?.[activeEditionId]
        ? editionsById[activeEditionId]
        : null;
    return getParticipantDisplayAge(
      { age: ageFromParticipant, dateOfBirth },
      edition?.year ?? null,
      edition?.date,
    );
  }, [ageFromParticipant, dateOfBirth, activeEditionId, editionsById]);

  const displayName = useMemo(() => {
    const fullName = `${lastName} ${firstName}`;
    return displayAge != null ? `${fullName} (${displayAge})` : fullName;
  }, [lastName, firstName, displayAge]);

  const isRegistered = !!(callbacks.registrationId || registration);
  const registrationData: IRegistration | null = registration || null;
  const registrationState = getParticipantRegistrationState(participant, checkinsById);

  const competitionId = registrationData?.competitionId || '';
  const distanceId = registrationData?.distanceId || '';
  const competition = competitionId
    ? (find(competitions, c => c.id === competitionId) as ICompetition)
    : null;
  const competitionName = competition?.i18n[currentLang] || '';
  const distance = distanceId ? (find(distances, d => d.id === distanceId) as TechnicalType) : null;
  const distanceName = distance?.i18n[currentLang] || '';

  const price = useMemo(() => {
    if (!isRegistered) return null;
    const lookupEditionId = registrationData?.editionId || activeEditionId || undefined;
    const edition = lookupEditionId ? editionsById?.[lookupEditionId] : undefined;
    return getRegistrationDisplayPrice(registrationData?.price, edition, {
      dateOfBirth,
      age: ageFromParticipant,
      discounted,
    });
  }, [
    isRegistered,
    registrationData?.price,
    registrationData?.editionId,
    activeEditionId,
    editionsById,
    dateOfBirth,
    ageFromParticipant,
    discounted,
  ]);

  const rawSerial = checkinData?.ser ?? (checkedIn && checkinsById?.[checkedIn]?.ser);
  const serialNumber =
    typeof rawSerial === 'number'
      ? rawSerial
      : typeof rawSerial === 'string' && rawSerial !== ''
        ? Number(rawSerial)
        : undefined;
  const hasValidSerial = serialNumber != null && !Number.isNaN(serialNumber);
  const serialDisplay = rawSerial ?? '-';
  const entryLabel = [competitionName, distanceName].filter(Boolean).join(' · ');

  const registerForEditionLabel =
    activeEditionId && editionsById?.[activeEditionId]
      ? t('registrations.registerForEdition', {
          eventName: `${t('base.defaultTitle')} ${editionsById[activeEditionId].year}`,
        })
      : undefined;

  const signupEditionId = registrationData?.editionId || activeEditionId || undefined;
  const signupEdition = signupEditionId ? editionsById?.[signupEditionId] : undefined;
  const signupInfo = formatRegistrationSignupInfo(
    t,
    signupEdition,
    registrationSignupAt(registrationData, participant.createdAt),
    dateFormat[currentLang],
  );

  return {
    t,
    id,
    displayName,
    price,
    competitionName,
    distanceName,
    serialDisplay,
    entryLabel,
    hasValidSerial,
    serialNumber,
    registrationState,
    isRegistered,
    signupInfo,
    registerForEditionLabel,
    rowRegisterTooltip: callbacks.registerTooltip ?? registerForEditionLabel ?? '',
    canEditRegistration: !!callbacks.editRegistration && registrationState === 'registered',
    canDeleteRegistration: !!callbacks.deleteRegistration && registrationState === 'registered',
    canCheckin: !!callbacks.checkin && registrationState === 'registered',
    showEntryMenu:
      (!!callbacks.checkin && registrationState === 'registered') ||
      (!!callbacks.editRegistration && registrationState === 'registered') ||
      (!!callbacks.deleteRegistration && registrationState === 'registered'),
    handlers: {
      register: (e?: MouseEvent<unknown>) => callbacks.register?.(id, e),
      editRegistration: (e?: MouseEvent<unknown>) => {
        e?.stopPropagation();
        callbacks.editRegistration?.(id, e);
      },
      deleteRegistration: (e?: MouseEvent<unknown>) => {
        e?.stopPropagation();
        callbacks.deleteRegistration?.(id, e);
      },
      checkin: (e?: MouseEvent<unknown>) => {
        e?.stopPropagation();
        callbacks.checkin?.(id, e);
      },
    },
  };
}
